import { useCallback, useEffect, useState } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { Loader2, LogOut, Headphones, BookOpen, Repeat, MonitorSmartphone, AlertTriangle, KeyRound, Gift } from "lucide-react";
import { toast } from "sonner";
import AuthPanel from "@/components/reader/AuthPanel";
import PassOffer from "@/components/reader/PassOffer";
import ReaderLibrary from "@/components/reader/ReaderLibrary";
import ReaderBookmarks from "@/components/reader/ReaderBookmarks";
import ReaderHighlights from "@/components/reader/ReaderHighlights";
import RenewBanner from "@/components/reader/RenewBanner";
import RecoveryCodeDialog from "@/components/reader/RecoveryCodeDialog";
import GiftRedeem, { GiftNote } from "@/components/reader/GiftRedeem";
import { readerMe, fetchLibrary, readerLogout, newRecoveryCode, daysUntil, errorDetail, setTimezone } from "@/lib/reader";

const FEATURES = [
  { icon: BookOpen, text: "All four Michael Leckie novels" },
  { icon: Headphones, text: "Male or female narrator — switch anytime" },
  { icon: Repeat, text: "Picks up exactly where you left off" },
  { icon: MonitorSmartphone, text: "One book open at a time, on one device" },
];

function NewCodeButton() {
  const [code, setCode] = useState(null);
  const create = async () => {
    if (!window.confirm("Create a new recovery code? Your old code will stop working.")) return;
    try {
      setCode((await newRecoveryCode()).recovery_code);
    } catch (e) {
      toast.error(errorDetail(e));
    }
  };
  return (
    <>
      {code && <RecoveryCodeDialog code={code} onDone={() => setCode(null)} />}
      <button onClick={create} data-testid="reader-new-recovery-code" className="flex items-center gap-2 font-mono-ui text-xs uppercase tracking-[0.18em] text-soft hover:text-primary-gold transition-colors">
        <KeyRound className="w-4 h-4" /> New recovery code
      </button>
    </>
  );
}

function GiftLink() {
  return (
    <Link to="/reader/gift" data-testid="reader-gift-link" className="flex items-center gap-2 font-mono-ui text-xs uppercase tracking-[0.18em] text-primary-gold hover:opacity-80 transition-opacity">
      <Gift className="w-4 h-4" /> Give it as a gift
    </Link>
  );
}

function SampleLink() {
  return (
    <Link to="/listen" data-testid="reader-sample-link" className="flex items-center gap-2 font-mono-ui text-xs uppercase tracking-[0.18em] text-primary-gold hover:opacity-80 transition-opacity">
      <Headphones className="w-4 h-4" /> Hear a free sample
    </Link>
  );
}

function PassBanner({ me, onLogout }) {
  const expires = new Date(me.pass.expires_at);
  const daysLeft = daysUntil(me.pass.expires_at);
  return (
    <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 border border-white/10 bg-[#121212]/80 px-6 py-4 mb-12" data-testid="reader-pass-banner">
      <p className="text-sm text-soft">
        <span className="text-text-primary">{me.user.name || me.user.email}</span> ·{" "}
        {me.pass.lifetime ? (
          <span className="text-primary-gold" data-testid="reader-pass-expiry">Complimentary access · no expiry</span>
        ) : (
          <>
            Pass active until <span className="text-primary-gold" data-testid="reader-pass-expiry">{expires.toLocaleDateString()}</span> ({daysLeft} days left)
          </>
        )}
      </p>
      <div className="flex flex-wrap items-center gap-6">
        <GiftLink />
        {me.user.has_password && <NewCodeButton />}
        <SignOut onLogout={onLogout} />
      </div>
    </div>
  );
}

function SignOut({ onLogout }) {
  return (
    <button onClick={onLogout} data-testid="reader-logout-button" className="flex items-center gap-2 font-mono-ui text-xs uppercase tracking-[0.18em] text-soft hover:text-primary-gold transition-colors">
      <LogOut className="w-4 h-4" /> Sign out
    </button>
  );
}

export default function Reader() {
  const [me, setMe] = useState(undefined);
  const [books, setBooks] = useState([]);
  const [giftNote, setGiftNote] = useState(null);
  const location = useLocation();
  const navigate = useNavigate();
  const notice = location.state?.notice;

  const refresh = useCallback(async () => {
    try {
      setMe(await readerMe());
      const tz = Intl.DateTimeFormat().resolvedOptions().timeZone;
      if (tz) setTimezone(tz).catch(() => {});
    } catch {
      setMe(null);
    }
  }, []);

  useEffect(() => {
    refresh();
    fetchLibrary().then(setBooks).catch(() => setBooks([]));
  }, [refresh]);

  const logout = async () => {
    await readerLogout().catch(() => {});
    toast.success("Signed out.");
    navigate("/reader", { replace: true, state: null });
    setMe(null);
  };

  const onGift = (res) => {
    if (res.message || res.from_name) setGiftNote(res);
    refresh();
  };

  const active = me?.pass?.active;

  return (
    <div className="relative pt-32 pb-12 px-6 sm:px-8 max-w-7xl mx-auto" data-testid="reader-page">
      {notice && (
        <div className="flex items-start gap-3 border border-[#e0982f]/40 bg-[#e0982f]/10 px-5 py-4 mb-10 text-sm" data-testid="reader-notice">
          <AlertTriangle className="w-4 h-4 text-[#e0982f] shrink-0 mt-0.5" /> {notice}
        </div>
      )}

      {active ? (
        <>
          <p className="font-mono-ui text-xs uppercase tracking-[0.25em] text-primary-gold">The Leckie E-Reader</p>
          <h1 className="font-serif-display text-4xl sm:text-5xl lg:text-6xl mt-4 mb-10">Your Library</h1>
          <PassBanner me={me} onLogout={logout} />
          {!me.pass.lifetime && <div className="-mt-8 mb-12"><GiftRedeem onRedeemed={onGift} /></div>}
          {giftNote && <GiftNote note={giftNote} onClose={() => setGiftNote(null)} />}
          {daysUntil(me.pass.expires_at) <= 3 && (
            <RenewBanner me={me} daysLeft={daysUntil(me.pass.expires_at)} onPaid={refresh} startOpen={!!location.state?.renew} />
          )}
          <ReaderLibrary books={books} state={me.state} userName={me.user.name} />
          <ReaderBookmarks books={books} bookmarks={me.state.bookmarks} onChanged={refresh} />
          <ReaderHighlights books={books} highlights={me.state.highlights} onChanged={refresh} />
        </>
      ) : (
        <div className="grid lg:grid-cols-12 gap-12 lg:gap-16 items-start">
          <div className="lg:col-span-7 fade-up">
            <p className="font-mono-ui text-xs uppercase tracking-[0.25em] text-primary-gold">The Leckie E-Reader</p>
            <h1 className="font-serif-display text-4xl sm:text-5xl lg:text-6xl mt-4 leading-[1.05]">
              Read it. <span className="text-primary-gold italic">Hear it.</span>
            </h1>
            <p className="text-soft text-base mt-6 max-w-xl leading-relaxed">
              Every Michael Leckie novel on screen and read aloud, page by page. One payment of $15 opens the whole
              library for 40 days.
            </p>
            <ul className="mt-10 space-y-4">
              {FEATURES.map(({ icon: Icon, text }) => (
                <li key={text} className="flex items-center gap-4 text-text-primary">
                  <span className="w-9 h-9 border border-primary-gold/40 flex items-center justify-center">
                    <Icon className="w-4 h-4 text-primary-gold" />
                  </span>
                  {text}
                </li>
              ))}
            </ul>
            <div className="mt-10 flex flex-wrap gap-8"><SampleLink /><GiftLink /></div>
          </div>
          <div className="lg:col-span-5 w-full">
            {me === undefined ? (
              <div className="flex justify-center py-24"><Loader2 className="w-6 h-6 text-primary-gold animate-spin" /></div>
            ) : me === null ? (
              <AuthPanel onSignedIn={() => { navigate("/reader", { replace: true, state: null }); refresh(); }} />
            ) : (
              <>
                <PassOffer user={me.user} price={me.price} days={me.days} onPaid={refresh} />
                <div className="mt-6 border border-white/10 bg-[#121212]/80 px-6 py-5"><GiftRedeem onRedeemed={onGift} /></div>
                <div className="mt-6 flex justify-end"><SignOut onLogout={logout} /></div>
              </>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
